import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, useWindowDimensions } from 'react-native';
import Animated, { Easing, SlideInDown, SlideOutDown, useSharedValue } from 'react-native-reanimated';

import ConfettiLottieJSON from '@/assets/animations/confetti.json';
import { Box } from '@/components/lib/components';
import LottieView from 'lottie-react-native';

import { useBetslipData } from '../../hooks/use-betslip-data';
import { spreadMultipliersOnWheel } from '../../utils/mystery-multiplier-utils';
import { DEFAULT_WHEEL_SIZE, MysteryWheel, MysteryWheelRef } from './MysteryWheel';
import { StackedMultiplierText, TEXT_LINE_HEIGHT } from './StackedMultiplierText';

export const WHEEL_NEGATIVE_OFFSET = -128;
const ANIMATED_MULTIPLIER_SPACING = 87;
export const WHEEL_EXIT_DURATION_MS = 180;
const CONFETTI_SIZE = 200;

const AnimatedBox = Animated.createAnimatedComponent(Box);

export const wheelEnteringAnimation = SlideInDown.springify().damping(15).stiffness(80);
export const wheelExitingAnimation = SlideOutDown.duration(WHEEL_EXIT_DURATION_MS).easing(Easing.in(Easing.cubic));

type Props = {
    showWheel: boolean;
    wheelRef: React.RefObject<MysteryWheelRef | null>;
    wheelStopped: boolean;
    shouldShowConfetti: boolean;
    onWheelStop: () => void;
    onExited: () => void;
};

export const MysteryWheelOverlay = memo(
    ({ showWheel, wheelRef, wheelStopped, shouldShowConfetti, onWheelStop, onExited }: Props) => {
        const { height } = useWindowDimensions();
        // when showing the wheel for the first time
        // we want it to come from the bottom of the screen (not outside the screen)
        const shortSlideInEntering = useMemo(
            () =>
                SlideInDown.springify()
                    .damping(15)
                    .stiffness(80)
                    .withInitialValues({ originY: height - (DEFAULT_WHEEL_SIZE + WHEEL_NEGATIVE_OFFSET) + 200 }),
            [height]
        );
        const rotation = useSharedValue(0);
        const firstShowUsed = useRef(false);
        const { mysteryMultipliers: mysteryMultipliersData } = useBetslipData();
        const mysteryMultipliers = useMemo(() => {
            const multipliers = mysteryMultipliersData?.multipliers ?? [];
            return spreadMultipliersOnWheel({ multipliers, wheelSize: 16 });
        }, [mysteryMultipliersData]);

        const confettiAnimRef = useRef<LottieView>(null);
        const [exited, setExited] = useState(false);

        useEffect(() => {
            if (wheelStopped && shouldShowConfetti) {
                confettiAnimRef.current?.play();
            }
        }, [wheelStopped, shouldShowConfetti]);

        // Fire onExited slightly before the exit animation finishes so the confirmation
        // starts appearing while the wheel is still animating out.
        // setExited waits for the full duration to unmount the overlay.
        useEffect(() => {
            if (showWheel) {
                setExited(false);
                return;
            }
            firstShowUsed.current = true;
            const confirmationTimeout = setTimeout(onExited, 0);
            const unmountTimeout = setTimeout(() => setExited(true), WHEEL_EXIT_DURATION_MS);
            return () => {
                clearTimeout(confirmationTimeout);
                clearTimeout(unmountTimeout);
            };
        }, [showWheel, onExited]);

        const enteringAnimation = firstShowUsed.current ? wheelEnteringAnimation : shortSlideInEntering;

        if (!showWheel && exited) return null;

        return (
            <>
                {showWheel ? (
                    <AnimatedBox
                        key={'wheel'}
                        position={'absolute'}
                        alignItems={'center'}
                        bottom={WHEEL_NEGATIVE_OFFSET}
                        left={0}
                        right={0}
                        entering={enteringAnimation}
                        exiting={wheelExitingAnimation}
                    >
                        <MysteryWheel
                            ref={wheelRef}
                            rotation={rotation}
                            onWheelStop={onWheelStop}
                            multipliers={mysteryMultipliers}
                        />
                    </AnimatedBox>
                ) : null}
                {showWheel ? (
                    <AnimatedBox
                        key={'multiplier'}
                        position={'absolute'}
                        alignItems={'center'}
                        left={0}
                        right={0}
                        style={{
                            bottom: DEFAULT_WHEEL_SIZE + WHEEL_NEGATIVE_OFFSET + ANIMATED_MULTIPLIER_SPACING,
                        }}
                        entering={enteringAnimation}
                        exiting={wheelExitingAnimation}
                    >
                        <Box alignItems={'center'} height={TEXT_LINE_HEIGHT}>
                            {mysteryMultipliers.map((multiplier, index) => (
                                <StackedMultiplierText
                                    key={index}
                                    multiplier={multiplier}
                                    index={index}
                                    rotation={rotation}
                                    sliceAngle={360 / mysteryMultipliers.length}
                                    totalSlices={mysteryMultipliers.length}
                                />
                            ))}
                        </Box>
                        <Box
                            position={'absolute'}
                            left={0}
                            right={0}
                            alignItems={'center'}
                            pointerEvents={'none'}
                            style={{
                                top: TEXT_LINE_HEIGHT / 2 - CONFETTI_SIZE / 2,
                                height: CONFETTI_SIZE,
                            }}
                        >
                            <LottieView
                                ref={confettiAnimRef}
                                source={ConfettiLottieJSON}
                                resizeMode={'cover'}
                                style={styles.confetti}
                                autoPlay={false}
                                loop={false}
                            />
                        </Box>
                    </AnimatedBox>
                ) : null}
            </>
        );
    }
);

const styles = StyleSheet.create({
    confetti: {
        width: CONFETTI_SIZE,
        height: CONFETTI_SIZE,
    },
});
